import { useMemo, useState } from 'react';
import { Plus, Trash2, ExternalLink, Pencil, Star } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAdminServices, useUpsertService, useDeleteService, type ServiceRow } from '@/lib/admin-queries';
import { ServiceEditForm } from '@/features/services/ServiceEditForm';
import { toast } from '@/components/ui/Toast';

export const SERVICES_MAX = 20;

const EMPTY: ServiceRow = {
  slug: '',
  title: '',
  summary: null,
  description_md: null,
  icon: null,
  deliverables: [],
  tech_strip: [],
  cta_label: 'Start a project',
  cta_href: '/contact?topic=new-project',
  display_order: 1,
  is_featured: false,
  meta_title: null,
  meta_description: null,
  status_publish: 'draft',
  topic_id: null,
  pricing: null,
};

export function ServicesPage() {
  const { data: rows = [], isLoading } = useAdminServices();
  const upsert = useUpsertService();
  const remove = useDeleteService();
  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const [deleting, setDeleting] = useState<ServiceRow | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Service',
        cell: (r: ServiceRow) => (
          <div>
            <p className="font-medium">{r.title}</p>
            <p className="text-xs text-fg-subtle">/{r.slug}</p>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (r: ServiceRow) => (
          <Badge variant={r.status_publish === 'published' ? 'success' : 'outline'}>
            {r.status_publish}
          </Badge>
        ),
      },
      {
        key: 'featured',
        header: 'Featured',
        cell: (r: ServiceRow) =>
          r.is_featured ? (
            <Star className="h-3.5 w-3.5 fill-gold-500 text-gold-500" />
          ) : (
            <span className="text-xs text-fg-subtle">—</span>
          ),
      },
      {
        key: 'display_order',
        header: 'Order',
        align: 'right' as const,
        cell: (r: ServiceRow) => <span className="text-xs text-fg-muted">{r.display_order}</span>,
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: ServiceRow) => (
          <div className="flex items-center justify-end gap-1">
            {r.status_publish === 'published' && (
              <a
                href={`https://vonlof.co/services#${r.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View live"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const atMax = rows.length >= SERVICES_MAX;

  async function onSave(row: ServiceRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Service updated' : 'Service created');
      setEditing(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Service deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  function handleNew() {
    if (atMax) {
      toast.error(`Maximum of ${SERVICES_MAX} services reached. Delete one to add another.`);
      return;
    }
    const nextOrder = rows.length > 0 ? Math.max(...rows.map((r) => r.display_order)) + 1 : 1;
    setEditing({ ...EMPTY, display_order: nextOrder });
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Services</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Capabilities</h1>
          <p className="mt-2 text-sm text-fg-muted">
            Manage the services shown on /services. {rows.length} of {SERVICES_MAX} used.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={handleNew} disabled={atMax}>
          New service
        </Button>
      </header>

      {atMax && (
        <div className="rounded-md border border-gold-500/30 bg-gold-500/5 px-4 py-3 text-xs text-fg-muted">
          Maximum of {SERVICES_MAX} services reached. Delete one to add another.
        </div>
      )}

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No services yet. Click New service to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing?.id ? 'Edit service' : 'New service'}
        size="xl"
      >
        {editing && (
          <ServiceEditForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => setEditing(null)}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this service?"
        description={deleting ? `"${deleting.title}" will be removed from /services.` : ''}
        confirmLabel="Delete service"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}
